import { ReactNode, Suspense } from "react";
import { lazyWithRetry } from "@/utils/lazy-with-retry";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation } from "react-router-dom";

// Lazy load components not needed on auth page
const Sidebar = lazyWithRetry(() => import("./Sidebar").then(module => ({
  default: module.Sidebar
})), "Sidebar");

const NotificationsBell = lazyWithRetry(() => import("./NotificationsBell").then(module => ({
  default: module.NotificationsBell
})), "NotificationsBell");

const ModeToggle = lazyWithRetry(() => import("@/components/ui/mode-toggle").then(module => ({
  default: module.ModeToggle
})), "ModeToggle");

interface AppLayoutProps {
  children: ReactNode;
  background?: 'default' | 'geometric' | 'gradient-flow' | 'mesh' | 'glow' | 'orb' | 'dots' | 'vibrant-grid';
}

export const AppLayout = ({ children, background = 'default' }: AppLayoutProps) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const isAuthPage = location.pathname === "/auth";

  const bgClass = background === 'default' ? 'bg-background' : `bg-${background}`;

  // Inline styles for vibrant-grid to ensure it works
  const vibrantGridStyle = background === 'vibrant-grid' ? {
    background: `
      /* Fine grid blocks - 10 horizontal sections (purple & white) */
      linear-gradient(90deg,
        hsl(262 83% 58% / 0.9) 0%, hsl(262 83% 58% / 0.9) 10%,
        hsl(0 0% 100% / 0.7) 10%, hsl(0 0% 100% / 0.7) 20%,
        hsl(262 83% 58% / 0.9) 20%, hsl(262 83% 58% / 0.9) 30%,
        hsl(0 0% 100% / 0.7) 30%, hsl(0 0% 100% / 0.7) 40%,
        hsl(262 83% 58% / 0.9) 40%, hsl(262 83% 58% / 0.9) 50%,
        hsl(0 0% 100% / 0.7) 50%, hsl(0 0% 100% / 0.7) 60%,
        hsl(262 83% 58% / 0.9) 60%, hsl(262 83% 58% / 0.9) 70%,
        hsl(0 0% 100% / 0.7) 70%, hsl(0 0% 100% / 0.7) 80%,
        hsl(262 83% 58% / 0.9) 80%, hsl(262 83% 58% / 0.9) 90%,
        hsl(0 0% 100% / 0.7) 90%, hsl(0 0% 100% / 0.7) 100%
      ),
      /* Vertical stripes - 8 sections (purple & white) */
      linear-gradient(180deg,
        hsl(0 0% 100% / 0.7) 0%, hsl(0 0% 100% / 0.7) 12.5%,
        hsl(262 83% 58% / 0.9) 12.5%, hsl(262 83% 58% / 0.9) 25%,
        hsl(0 0% 100% / 0.7) 25%, hsl(0 0% 100% / 0.7) 37.5%,
        hsl(262 83% 58% / 0.9) 37.5%, hsl(262 83% 58% / 0.9) 50%,
        hsl(0 0% 100% / 0.7) 50%, hsl(0 0% 100% / 0.7) 62.5%,
        hsl(262 83% 58% / 0.9) 62.5%, hsl(262 83% 58% / 0.9) 75%,
        hsl(0 0% 100% / 0.7) 75%, hsl(0 0% 100% / 0.7) 87.5%,
        hsl(262 83% 58% / 0.9) 87.5%, hsl(262 83% 58% / 0.9) 100%
      ),
      /* Base - light purple for brightness */
      linear-gradient(135deg, hsl(262 83% 65%) 0%, hsl(250 100% 99%) 100%)
    `,
    backgroundAttachment: 'fixed',
    backgroundBlendMode: 'lighten'
  } : {};

  return (
    <div className={`h-screen ${background !== 'vibrant-grid' ? bgClass : 'bg-background'} ${isAuthPage ? '' : 'flex'}`} style={vibrantGridStyle}>
      {/* Skip to main content link for keyboard accessibility */}
      {!isAuthPage && (
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
        >
          Skip to main content
        </a>
      )}

      {/* Only show sidebar when not on auth page - lazy loaded */}
      {!isAuthPage && (
        <Suspense fallback={
          <aside className="hidden md:flex flex-col w-64 h-screen bg-background/80 backdrop-blur-xl border-r border-border/10 shadow-lg">
            <div className="flex h-20 items-center border-b border-border/10 px-6">
              <div className="flex items-center gap-3 w-full">
                <div className="h-9 w-9 bg-muted rounded-lg animate-pulse"></div>
                <div className="h-7 flex-1 bg-muted rounded animate-pulse"></div>
              </div>
            </div>
            <div className="px-3 py-4 flex-1 space-y-2 animate-pulse">
              <div className="h-10 bg-muted rounded-lg"></div>
              <div className="space-y-1.5">
                <div className="h-6 bg-muted rounded-lg w-3/4"></div>
                <div className="h-6 bg-muted rounded-lg w-4/5"></div>
                <div className="h-6 bg-muted rounded-lg w-3/5"></div>
                <div className="h-6 bg-muted rounded-lg w-2/3"></div>
                <div className="h-6 bg-muted rounded-lg w-3/4"></div>
              </div>
            </div>
          </aside>
        }>
          <Sidebar />
        </Suspense>
      )}

      {/* Main content with responsive layout */}
      <div className={`
        ${isAuthPage ? 'w-full h-screen' : 'flex-1 h-full w-full'}
        overflow-x-hidden overflow-y-auto
        ${isMobile && !isAuthPage ? 'pt-16 pb-20' : ''}
      `}>
        {/* Top navigation with notifications for non-auth pages - lazy loaded */}
        {!isAuthPage && (
          <div className="flex justify-end p-3 md:p-4">
            <Suspense fallback={
              <div className="w-8 h-8 animate-pulse bg-muted rounded-full"></div>
            }>
              <NotificationsBell />
            </Suspense>

          </div>
        )}

        <main
          id="main-content"
          className={`
          w-full
          py-4 md:py-6 px-4 md:px-6 lg:px-8
          max-w-full ${!isAuthPage && 'lg:max-w-[1400px] mx-auto'}
          ${isAuthPage ? 'max-w-md mx-auto' : ''}
        `}>
          {children}
        </main>
      </div>
    </div>
  );
};